/**
 * Reports — live sales, agent, provider, commission and float reports.
 */
import { useState } from "react";
import { PageHead } from "../components/PageHead";
import { ErrorCard, LoadingCard } from "../components/Async";
import { useAdminQuery, downloadCsv, shortDate, tzs } from "../lib/admin";
import type {
  AgentReportRow,
  CommissionReport,
  FloatReport,
  ProviderReportRow,
  SalesReport,
} from "../lib/types";
import { BarChart3, Download, Users, Server, Percent, Wallet } from "lucide-react";

type Tab = "sales" | "agents" | "providers" | "commission" | "float";

const TABS: Array<{ key: Tab; label: string; icon: React.ReactNode }> = [
  { key: "sales", label: "Sales", icon: <BarChart3 size={14} /> },
  { key: "agents", label: "Agents", icon: <Users size={14} /> },
  { key: "providers", label: "Providers", icon: <Server size={14} /> },
  { key: "commission", label: "Commission", icon: <Percent size={14} /> },
  { key: "float", label: "Float", icon: <Wallet size={14} /> },
];

const PERIODS = [
  { value: "daily", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
] as const;

export function Reports() {
  const [tab, setTab] = useState<Tab>("sales");
  const [period, setPeriod] = useState<(typeof PERIODS)[number]["value"]>("7d");

  const sales = useAdminQuery<SalesReport>(["report-sales", period], "/admin/v1/reports/sales", { period });
  const agents = useAdminQuery<{ data: AgentReportRow[]; total: number }>(["report-agents"], "/admin/v1/reports/agents");
  const providers = useAdminQuery<{ data: ProviderReportRow[] }>(["report-providers"], "/admin/v1/reports/providers");
  const commission = useAdminQuery<CommissionReport>(["report-commission", period], "/admin/v1/reports/commission", { period });
  const float = useAdminQuery<FloatReport>(["report-float"], "/admin/v1/reports/float");

  const maxDaily = sales.data ? Math.max(1, ...sales.data.daily.map((d) => d.gross_minor)) : 1;

  return (
    <>
      <PageHead
        title="Reports"
        description="Management and agent reports with CSV export."
        actions={
          tab === "sales" || tab === "commission" ? (
            <div className="admin-toggle-group">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  className={`admin-toggle ${period === p.value ? "admin-toggle-active" : ""}`}
                  onClick={() => setPeriod(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          ) : undefined
        }
      />

      <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`ev-btn ev-btn-sm ${tab === t.key ? "ev-btn-primary" : "ev-btn-outline"}`}
            onClick={() => setTab(t.key)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === "sales" && (
        <>
          {sales.isLoading && <LoadingCard label="Loading sales report…" />}
          {sales.error && <ErrorCard error={sales.error} onRetry={() => void sales.refetch()} />}
          {sales.data && (
            <>
              <div className="admin-kpi-grid" style={{ marginBottom: 24 }}>
                <div className="admin-card">
                  <span className="ev-body-muted">Gross sales</span>
                  <div className="ev-display-s" style={{ color: "var(--ev-mint-600)" }}>{tzs(sales.data.sales.gross_minor)}</div>
                </div>
                <div className="admin-card">
                  <span className="ev-body-muted">Orders</span>
                  <div className="ev-display-s">{sales.data.sales.orders}</div>
                  <span className="ev-body-micro muted">{sales.data.sales.success} success</span>
                </div>
                <div className="admin-card">
                  <span className="ev-body-muted">Commission</span>
                  <div className="ev-display-s" style={{ color: "var(--ev-blue-600)" }}>{tzs(sales.data.sales.commission_minor)}</div>
                </div>
                <div className="admin-card">
                  <span className="ev-body-muted">Average sale</span>
                  <div className="ev-display-s">{tzs(sales.data.sales.average_sale_minor)}</div>
                </div>
              </div>

              <div className="admin-row-6" style={{ marginBottom: 24 }}>
                <div className="admin-card">
                  <div className="admin-card-header">
                    <h3>Daily gross</h3>
                    <button
                      className="ev-btn ev-btn-sm ev-btn-outline"
                      onClick={() =>
                        downloadCsv(
                          "evocha-sales-report.csv",
                          ["Date", "Orders", "Gross", "Commission"],
                          sales.data!.daily.map((d) => [d.date, d.orders, d.gross_minor / 100, d.commission_minor / 100]),
                        )
                      }
                    >
                      <Download size={14} /> CSV
                    </button>
                  </div>
                  <div className="admin-sales-chart" style={{ marginTop: 8 }}>
                    {sales.data.daily.map((d) => (
                      <div key={d.date} className="admin-bar-item">
                        <div className="admin-bar-track">
                          <div className="admin-bar" style={{ height: `${(d.gross_minor / maxDaily) * 100}%` }} />
                        </div>
                        <span className="ev-body-micro">{shortDate(d.date)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="admin-card">
                  <h3>Network mix</h3>
                  <div className="admin-sales-breakdown" style={{ marginTop: 12 }}>
                    {sales.data.network_mix.length === 0 && <span className="ev-body-micro muted">No successful sales in this period.</span>}
                    {sales.data.network_mix.map((n) => (
                      <div key={n.mno} className="admin-sales-breakdown-row">
                        <span className="ev-body-micro">{n.mno} · {n.count} orders</span>
                        <span className="ev-body-micro" style={{ fontWeight: 600 }}>{tzs(n.gross_minor)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th className="admin-table-th">Date</th>
                        <th className="admin-table-th">Orders</th>
                        <th className="admin-table-th">Gross</th>
                        <th className="admin-table-th">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sales.data.daily.map((d) => (
                        <tr key={d.date} className="admin-table-row-hover">
                          <td className="admin-table-td" data-label="Date">{shortDate(d.date)}</td>
                          <td className="admin-table-td" data-label="Orders">{d.orders}</td>
                          <td className="admin-table-td" data-label="Gross"><span className="ev-body-mono ev-body-semibold">{tzs(d.gross_minor)}</span></td>
                          <td className="admin-table-td" data-label="Commission"><span className="ev-body-mono" style={{ color: "var(--ev-blue-600)" }}>{tzs(d.commission_minor)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {tab === "agents" && (
        <>
          {agents.isLoading && <LoadingCard label="Loading agent report…" />}
          {agents.error && <ErrorCard error={agents.error} onRetry={() => void agents.refetch()} />}
          {agents.data && (
            <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
              <div className="admin-card-header" style={{ padding: "16px 20px", marginBottom: 0 }}>
                <h3>Agent summary (all time)</h3>
                <button
                  className="ev-btn ev-btn-sm ev-btn-outline"
                  onClick={() =>
                    downloadCsv(
                      "evocha-agents-report.csv",
                      ["Code", "Business", "Region", "Status", "KYC", "Orders", "Gross", "Commission"],
                      agents.data!.data.map((a) => [a.code, a.business_name, a.region, a.status, a.kyc_status, a.orders, a.gross_minor / 100, a.commission_minor / 100]),
                    )
                  }
                >
                  <Download size={14} /> CSV
                </button>
              </div>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th className="admin-table-th">Agent</th>
                      <th className="admin-table-th">Region</th>
                      <th className="admin-table-th">Status</th>
                      <th className="admin-table-th">Orders</th>
                      <th className="admin-table-th">Gross</th>
                      <th className="admin-table-th">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.data.data.map((a) => (
                      <tr key={a.agent_id} className="admin-table-row-hover">
                        <td className="admin-table-td" data-label="Agent">
                          <div className="admin-cell-stack">
                            <span className="ev-body-semibold">{a.business_name || a.code}</span>
                            <span className="ev-body-micro muted">{a.code}</span>
                          </div>
                        </td>
                        <td className="admin-table-td" data-label="Region">{a.region || "—"}</td>
                        <td className="admin-table-td" data-label="Status"><span className={`ev-badge ${a.status === "active" ? "ev-badge-mint" : "ev-badge-amber"}`}>{a.status}</span></td>
                        <td className="admin-table-td" data-label="Orders">{a.orders}</td>
                        <td className="admin-table-td" data-label="Gross"><span className="ev-body-mono ev-body-semibold">{tzs(a.gross_minor)}</span></td>
                        <td className="admin-table-td" data-label="Commission"><span className="ev-body-mono" style={{ color: "var(--ev-blue-600)" }}>{tzs(a.commission_minor)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "providers" && (
        <>
          {providers.isLoading && <LoadingCard label="Loading provider report…" />}
          {providers.error && <ErrorCard error={providers.error} onRetry={() => void providers.refetch()} />}
          {providers.data && (
            <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
              <div className="admin-card-header" style={{ padding: "16px 20px", marginBottom: 0 }}>
                <h3>Provider performance</h3>
                <button
                  className="ev-btn ev-btn-sm ev-btn-outline"
                  onClick={() =>
                    downloadCsv(
                      "evocha-providers-report.csv",
                      ["Provider", "Total", "Success", "Failed", "Pending", "Review", "Success rate", "Gross"],
                      providers.data!.data.map((p) => [p.provider, p.total, p.success, p.failed, p.pending, p.review, p.success_rate, p.gross_minor / 100]),
                    )
                  }
                >
                  <Download size={14} /> CSV
                </button>
              </div>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th className="admin-table-th">Provider</th>
                      <th className="admin-table-th">Total</th>
                      <th className="admin-table-th">Success</th>
                      <th className="admin-table-th">Failed</th>
                      <th className="admin-table-th">Pending</th>
                      <th className="admin-table-th">Success rate</th>
                      <th className="admin-table-th">Gross</th>
                    </tr>
                  </thead>
                  <tbody>
                    {providers.data.data.map((p) => (
                      <tr key={p.provider} className="admin-table-row-hover">
                        <td className="admin-table-td" data-label="Provider"><div className="admin-cell-stack"><span className="ev-body-semibold">{p.provider}</span></div></td>
                        <td className="admin-table-td" data-label="Total">{p.total}</td>
                        <td className="admin-table-td" data-label="Success"><span style={{ color: "var(--ev-mint-600)" }}>{p.success}</span></td>
                        <td className="admin-table-td" data-label="Failed"><span style={{ color: "var(--ev-red-600)" }}>{p.failed}</span></td>
                        <td className="admin-table-td" data-label="Pending"><span style={{ color: "var(--ev-amber-600)" }}>{p.pending}</span></td>
                        <td className="admin-table-td" data-label="Success rate"><strong>{p.success_rate}%</strong></td>
                        <td className="admin-table-td" data-label="Gross"><span className="ev-body-mono ev-body-semibold">{tzs(p.gross_minor)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "commission" && (
        <>
          {commission.isLoading && <LoadingCard label="Loading commission report…" />}
          {commission.error && <ErrorCard error={commission.error} onRetry={() => void commission.refetch()} />}
          {commission.data && (
            <>
              <div className="admin-kpi-grid" style={{ marginBottom: 24 }}>
                <div className="admin-card">
                  <span className="ev-body-muted">Total commission</span>
                  <div className="ev-display-s" style={{ color: "var(--ev-blue-600)" }}>{tzs(commission.data.total_commission_minor)}</div>
                </div>
                <div className="admin-card">
                  <span className="ev-body-muted">Agent count</span>
                  <div className="ev-display-s">{commission.data.agents.length}</div>
                </div>
              </div>
              <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
                <div className="admin-card-header" style={{ padding: "16px 20px", marginBottom: 0 }}>
                  <h3>Commission by agent</h3>
                  <button
                    className="ev-btn ev-btn-sm ev-btn-outline"
                    onClick={() =>
                      downloadCsv(
                        "evocha-commission-report.csv",
                        ["Code", "Business", "Commission"],
                        commission.data!.agents.map((a) => [a.code, a.business_name, a.commission_minor / 100]),
                      )
                    }
                  >
                    <Download size={14} /> CSV
                  </button>
                </div>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th className="admin-table-th">Agent</th>
                        <th className="admin-table-th">Code</th>
                        <th className="admin-table-th">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commission.data.agents.map((a) => (
                        <tr key={a.agent_id} className="admin-table-row-hover">
                          <td className="admin-table-td" data-label="Agent"><span className="ev-body-semibold">{a.business_name || a.code}</span></td>
                          <td className="admin-table-td" data-label="Code"><span className="ev-body-mono">{a.code}</span></td>
                          <td className="admin-table-td" data-label="Commission"><span className="ev-body-mono ev-body-semibold">{tzs(a.commission_minor)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {tab === "float" && (
        <>
          {float.isLoading && <LoadingCard label="Loading float report…" />}
          {float.error && <ErrorCard error={float.error} onRetry={() => void float.refetch()} />}
          {float.data && (
            <>
              <div className="admin-kpi-grid" style={{ marginBottom: 24 }}>
                <div className="admin-card">
                  <span className="ev-body-muted">Available float</span>
                  <div className="ev-display-s" style={{ color: "var(--ev-mint-600)" }}>{tzs(float.data.totals.available_minor)}</div>
                </div>
                <div className="admin-card">
                  <span className="ev-body-muted">Reserved</span>
                  <div className="ev-display-s" style={{ color: "var(--ev-amber-600)" }}>{tzs(float.data.totals.reserved_minor)}</div>
                </div>
                <div className="admin-card">
                  <span className="ev-body-muted">Commission pool</span>
                  <div className="ev-display-s" style={{ color: "var(--ev-blue-600)" }}>{tzs(float.data.totals.commission_minor)}</div>
                </div>
                <div className="admin-card">
                  <span className="ev-body-muted">Provider payable</span>
                  <div className="ev-display-s">{tzs(float.data.totals.provider_payable_minor)}</div>
                </div>
              </div>
              <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
                <div className="admin-card-header" style={{ padding: "16px 20px", marginBottom: 0 }}>
                  <h3>Float by wallet</h3>
                  <button
                    className="ev-btn ev-btn-sm ev-btn-outline"
                    onClick={() =>
                      downloadCsv(
                        "evocha-float-report.csv",
                        ["Wallet", "Agent", "Available", "Reserved", "Commission"],
                        float.data!.wallets.map((w) => [w.wallet_id, w.agent?.business_name ?? "", w.available_minor / 100, w.reserved_minor / 100, w.commission_minor / 100]),
                      )
                    }
                  >
                    <Download size={14} /> CSV
                  </button>
                </div>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th className="admin-table-th">Agent</th>
                        <th className="admin-table-th">Wallet</th>
                        <th className="admin-table-th">Available</th>
                        <th className="admin-table-th">Reserved</th>
                        <th className="admin-table-th">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {float.data.wallets.map((w) => (
                        <tr key={w.wallet_id} className="admin-table-row-hover">
                          <td className="admin-table-td" data-label="Agent"><span className="ev-body-semibold">{w.agent?.business_name ?? w.agent?.code ?? "—"}</span></td>
                          <td className="admin-table-td" data-label="Wallet"><span className="ev-body-mono ev-body-micro hidden-mobile">{w.wallet_id}</span></td>
                          <td className="admin-table-td" data-label="Available"><span className="ev-body-mono" style={{ color: "var(--ev-mint-600)" }}>{tzs(w.available_minor)}</span></td>
                          <td className="admin-table-td" data-label="Reserved"><span className="ev-body-mono" style={{ color: "var(--ev-amber-600)" }}>{tzs(w.reserved_minor)}</span></td>
                          <td className="admin-table-td" data-label="Commission"><span className="ev-body-mono" style={{ color: "var(--ev-blue-600)" }}>{tzs(w.commission_minor)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}
